import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw, X, XCircle } from "lucide-react";
import { appealsApi } from "./api";
import type { BanAppeal } from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field, Link } from "../applications/shared/InfoBlock";
import { formatDate, formatDateTime } from "../applications/shared/format";
import { personDetail, personName, rutTailLabel } from "./shared/person";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import {
  APPEAL_STATUS_LABELS,
  INCIDENT_CATEGORY_LABELS,
  appealStatusTone,
} from "./shared/labels";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";

export function AppealsScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ["governance", "appeals"],
    queryFn: () => appealsApi.list(),
  });

  const selected = useMemo(
    () =>
      selectedId !== null ? (listQuery.data ?? []).find((a) => a.id === selectedId) ?? null : null,
    [listQuery.data, selectedId],
  );

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Governance · Apelaciones</p>
          <h1 className="nx-h1">Apelaciones a bans globales</h1>
          <p className="text-sm text-fg-muted mt-1">
            Usuarios que pidieron revisar su ban global. Aprobar = revoca el
            ban automáticamente. Rechazar = mantiene el ban y envía email al
            usuario con la decisión.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando apelaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<BanAppeal>
          rows={listQuery.data ?? []}
          columns={columns}
          page={1}
          pageSize={(listQuery.data ?? []).length || 1}
          total={(listQuery.data ?? []).length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
          emptyLabel="No hay apelaciones pendientes."
        />
      )}

      {selected && <AppealDrawer data={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

const columns: Column<BanAppeal>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "user",
    header: "Persona que apela",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{personName(r.user, r.subject)}</div>
        <div className="text-2xs text-fg-subtle break-all">
          {r.source === "rut" ? `Por RUT · ${r.contactEmail ?? "sin correo"}` : personDetail(r.user, r.subject)}
        </div>
      </div>
    ),
  },
  {
    key: "ban",
    header: "Ban apelado",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">
          Ban #{r.globalBan?.id ?? "—"} ·{" "}
          {r.globalBan?.category ? INCIDENT_CATEGORY_LABELS[r.globalBan.category] : "—"}
        </div>
        <div className="text-2xs text-fg-subtle">
          Baneado el {r.globalBan?.bannedAt ? formatDate(r.globalBan.bannedAt) : "—"}
        </div>
      </div>
    ),
  },
  {
    key: "message",
    header: "Mensaje",
    cell: (r) => (
      <div className="text-2xs text-fg-muted line-clamp-2 max-w-md">{r.userMessage}</div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={appealStatusTone(r.status)}
        label={APPEAL_STATUS_LABELS[r.status]}
      />
    ),
    width: "min",
  },
  {
    key: "createdAt",
    header: "Enviada",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

function AppealDrawer({ data, onClose }: { data: BanAppeal; onClose: () => void }) {
  const qc = useQueryClient();
  const { push } = useToast();

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const resolve = useMutation({
    mutationFn: ({ approve, reason }: { approve: boolean; reason: string }) =>
      appealsApi.resolve(data.id, approve, reason),
    onSuccess: (res) => {
      push({
        kind: "success",
        message:
          res.status === "approved_revoke"
            ? "Apelación aprobada. Ban global revocado y usuario notificado."
            : "Apelación rechazada. Usuario notificado con tu decisión.",
      });
      qc.invalidateQueries({ queryKey: ["governance", "appeals"] });
      qc.invalidateQueries({ queryKey: ["governance", "global-bans"] });
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo resolver." }),
  });

  const isPending = data.status === "pending";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[600px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">Apelación #{data.id}</h2>
            <p className="text-2xs text-fg-subtle mt-0.5">
              {personName(data.user, data.subject)} · {formatDateTime(data.createdAt)}
            </p>
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
              status={data.status}
              tone={appealStatusTone(data.status)}
              label={APPEAL_STATUS_LABELS[data.status]}
            />
            {data.source === "rut" && (
              <span className="text-2xs text-warning inline-flex items-center gap-1">
                <AlertTriangle size={10} /> Llegó por RUT: identidad sin verificar
              </span>
            )}
            {data.globalBan && !data.globalBan.appealable && (
              <span className="text-2xs text-danger inline-flex items-center gap-1">
                <AlertTriangle size={10} /> Ban marcado como NO apelable
              </span>
            )}
          </div>

          {data.source === "rut" && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg">
              Esta apelación llegó por el formulario público con RUT. Antes de decidir, verifica la
              identidad: la persona recibió un correo que le pide responder a soporte@ con una foto de
              su carnet por ambos lados. La decisión se envía a su correo de contacto.
            </div>
          )}

          <Section title="Persona que apela">
            <Field label="Nombre" value={personName(data.user, data.subject)} wide />
            {data.contactEmail && (
              <Field
                label="Correo de contacto"
                value={
                  <a className="text-accent hover:underline" href={`mailto:${data.contactEmail}`}>
                    {data.contactEmail}
                  </a>
                }
                wide
              />
            )}
            <Field
              label="Email"
              value={
                data.user?.email ? (
                  <a className="text-accent hover:underline" href={`mailto:${data.user.email}`}>
                    {data.user.email}
                  </a>
                ) : "—"
              }
              wide
            />
            <Field label="User ID" value={data.user?.id ?? "—"} />
            <Field label="RUT" value={rutTailLabel(data.subject) ?? "—"} />
          </Section>

          <Section title="Ban apelado">
            <Field label="Ban ID" value={data.globalBan ? `#${data.globalBan.id}` : "—"} />
            <Field
              label="Categoría"
              value={
                data.globalBan?.category
                  ? INCIDENT_CATEGORY_LABELS[data.globalBan.category]
                  : "—"
              }
            />
            <Field
              label="Baneado el"
              value={data.globalBan?.bannedAt ? formatDateTime(data.globalBan.bannedAt) : "—"}
              wide
            />
            <Field
              label="Expira"
              value={
                data.globalBan?.expiresAt
                  ? formatDateTime(data.globalBan.expiresAt)
                  : "Perpetuo"
              }
            />
            <Field label="Apelable" value={data.globalBan?.appealable ? "Sí" : "No"} />
            <Field
              label="Motivo público"
              value={data.globalBan?.publicReason ?? "—"}
              wide
            />
          </Section>

          <Section title="Mensaje del apelante">
            <Field
              label="Contenido"
              value={<p className="whitespace-pre-wrap text-fg">{data.userMessage}</p>}
              wide
            />
            {data.userEvidenceUrls && data.userEvidenceUrls.length > 0 && (
              <Field
                label={`Evidencia adjunta (${data.userEvidenceUrls.length})`}
                value={
                  <div className="flex flex-col gap-1">
                    {data.userEvidenceUrls.map((url, i) => (
                      <Link key={i} href={url}>
                        Archivo {i + 1}
                      </Link>
                    ))}
                  </div>
                }
                wide
              />
            )}
          </Section>

          {data.status !== "pending" && (
            <Section title="Decisión Nexus">
              <Field label="Revisado por" value={data.reviewedBy?.email ?? "—"} wide />
              <Field label="Cuándo" value={formatDateTime(data.reviewedAt)} />
              <Field
                label="Motivo"
                value={
                  data.decisionReason ? (
                    <p className="whitespace-pre-wrap text-fg">{data.decisionReason}</p>
                  ) : "—"
                }
                wide
              />
            </Section>
          )}
        </div>

        {isPending && (
          <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Rechazar apelación",
                  message: "El ban se mantiene. Se envía email al usuario con tu decisión.",
                  promptLabel: "Motivo del rechazo (obligatorio, queda en el email)",
                  confirmLabel: "Rechazar",
                  tone: "danger",
                });
                if (reason !== null && (reason as string).trim())
                  resolve.mutate({ approve: false, reason: (reason as string).trim() });
              }}
              disabled={resolve.isPending}
              className="nx-btn-danger"
            >
              <XCircle size={14} /> Rechazar apelación
            </button>
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Aprobar apelación",
                  message: `Se REVOCA el ban global de ${personName(data.user, data.subject)} inmediatamente. Recupera acceso a todos los clubes.`,
                  promptLabel: "Motivo de la decisión (obligatorio)",
                  confirmLabel: "Aprobar y revocar ban",
                  tone: "success",
                });
                if (reason !== null && (reason as string).trim())
                  resolve.mutate({ approve: true, reason: (reason as string).trim() });
              }}
              disabled={resolve.isPending}
              className="nx-btn-primary"
            >
              <CheckCircle2 size={14} /> Aprobar (revoca ban)
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
